import { createContext, useContext, type MouseEvent, type ReactNode } from 'react';
import type { ApiClient, ApiError } from '@aithora/shared';
import { sx } from '../../ui/designStyles';
import { useSession } from '../../session/SessionContext';
import { useQuery, type QueryState } from '../../useQuery';
import type { SettingsLens, SectionName } from './lens';

export { sx };

/**
 * The design renders every {{ value }} as its own span (dc runtime walkText), so inside a
 * flex row a value is a flex item of its own. Null and booleans render nothing.
 */
export function I(x: unknown): ReactNode {
  if (x === null || x === undefined || typeof x === 'boolean') return null;
  return <span>{String(x)}</span>;
}

/** The design's anchors never navigate. */
export const pre = (e?: MouseEvent | { preventDefault?: () => void }) => {
  if (e && e.preventDefault) e.preventDefault();
};

/** Load one route with the session's client. */
export function useApi<T>(load: (client: ApiClient, signal: AbortSignal) => Promise<T>, deps: unknown[] = [], enabled = true): QueryState<T> {
  const { client, session } = useSession();
  return useQuery<T>((signal) => load(client, signal), [client, session?.token, ...deps], { enabled });
}

/** A PROPOSED route answers 404 until the back end serves it: "not served", not an error. */
export const notServed = (e: ApiError | null): boolean => !!e && (e as { status?: number }).status === 404;

// ── The design's style helpers (settingsVals L21737-21747), value for value ──

export const pill = (on: boolean) =>
  'display:inline-flex;align-items:center;height:32px;padding:0 15px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' +
  (on ? 'background:#2F5C97;color:#fff;' : 'background:#FCFAF4;color:#54606C;border:1px solid rgba(34,39,46,.14);');
export const seg = (on: boolean) =>
  'display:inline-flex;align-items:center;height:28px;padding:0 12px;border-radius:6px;font-size:16px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' +
  (on ? 'background:#2F5C97;color:#fff;' : 'background:transparent;color:#54606C;border:1px solid rgba(34,39,46,.14);');
export const chip = (fg: string, bg: string) =>
  'font-size:14px;font-weight:700;letter-spacing:.07em;border-radius:6px;padding:3px 8px;flex:0 0 auto;white-space:nowrap;color:' + fg + ';background:' + bg + ';';
export const btn = (kind: 'primary' | 'soft' | 'off' | 'ghost' | boolean) =>
  'display:inline-flex;align-items:center;justify-content:center;box-sizing:border-box;height:30px;padding:0 14px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' +
  (kind === 'primary'
    ? 'background:#2F5C97;color:#fff;'
    : kind === 'soft'
      ? 'background:rgba(79,138,214,.1);color:#2F5C97;border:1px solid rgba(47,92,151,.3);'
      : kind === 'off'
        ? 'background:rgba(34,39,46,.05);color:#54606C;border:1px dashed rgba(34,39,46,.2);'
        : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.18);');
export const tileWrap = (i: number, n: number) => 'padding:12px 15px;box-sizing:border-box;' + (i < n - 1 ? 'border-right:1px solid rgba(34,39,46,.09);' : '');
export const fig = (c: string, px?: number) => 'font-size:' + (px || 26) + 'px;font-weight:700;line-height:1;color:' + c + ';';
export const CAPT = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;';

/** A small chip with tabular figures (chipT, L22639). */
export const chipT = (c: string, bg: string) =>
  'display:inline-flex;align-items:center;height:19px;padding:0 8px;border-radius:5px;font-variant-numeric:tabular-nums;font-size:14px;letter-spacing:.06em;font-weight:700;white-space:nowrap;color:' + c + ';background:' + bg + ';';

/** A read-only rule row's LOCKED tag (L22930). */
export const LOCKED_TAG = { tag: 'LOCKED', tagStyle: chip('#54606C', 'rgba(34,39,46,.09)') };

/** "Not served yet": the block says so rather than drawing made-up rows. */
export function Waiting({ what, style }: { what: string; style?: string }) {
  return (
    <div data-testid="settings-waiting" style={sx('font-size:16px;color:#54606C;line-height:1.5;' + (style ?? 'margin-top:8px;'))}>
      {what} is not served by the back end yet, so nothing is shown here.
    </div>
  );
}

/** A request's error, one line, in the design's error red. */
export function ErrLine({ error, style }: { error: ApiError | null | undefined; style?: string }) {
  if (!error) return null;
  return (
    <div role="status" aria-live="polite" style={sx('font-size:16px;color:#B3261E;line-height:1.5;' + (style ?? 'margin-top:8px;'))}>
      {error.message}
      {error.requestId ? ` (request ${error.requestId})` : ''}
    </div>
  );
}

/**
 * A section that could not load: the server's sentence, its request id and a retry. Polite
 * (role status, not alert), so it never competes with the emergency line for attention.
 */
export function LoadError({ error, onRetry }: { error: ApiError; onRetry: () => void }) {
  return (
    <div role="status" aria-live="polite" data-testid="settings-load-error" style={sx('display:flex;align-items:center;gap:12px;flex-wrap:wrap;padding:12px 2px;font-size:16px;color:#B3261E;line-height:1.5;')}>
      <span>
        {error.message}
        {error.requestId ? ` (request ${error.requestId})` : ''}
      </span>
      <button type="button" onClick={onRetry} style={sx('height:30px;padding:0 12px;border-radius:8px;border:1px solid rgba(34,39,46,.18);background:#FCFAF4;color:#3C454F;font-size:16px;font-weight:600;font-family:inherit;cursor:pointer;')}>
        Try again
      </button>
    </div>
  );
}

// ── The page's shared services ──────────────────────────────────────────────

export interface AskSpec {
  sec: string;
  title: string;
  body: string;
  btn: string;
  /** Sends the request with confirm: true; throw to keep the dialog open with the error. */
  go: () => Promise<void>;
  /** Extra fields inside the dialog (a reason, a code). */
  extra?: ReactNode;
}

export interface SettingsPageCtx {
  lens: SettingsLens;
  /** The signed-in person's building (null for Aithora's team). */
  facilityId: string | null;
  sec: SectionName;
  /** The reason writes are off (read-only role, View as), or null. */
  roReason: string | null;
  /** Open the confirm-first dialog (handoff 10b). */
  ask: (a: AskSpec) => void;
  /** A change was written: re-read the section's history. */
  changed: () => void;
  /** The Organization save's "Saved hh:mm by you · Undo" (10 s). */
  saved: (s: { t: string; undo: () => Promise<void> }) => void;
}

export const PageCtx = createContext<SettingsPageCtx | null>(null);
export function usePage(): SettingsPageCtx {
  const c = useContext(PageCtx);
  if (!c) throw new Error('usePage outside Settings');
  return c;
}

/**
 * A write handler that does nothing (and says why) while writes are off. The design's
 * controls keep their look; the control carries the reason for screen readers and on
 * hover, and the server refuses the write too.
 */
export function useGuarded() {
  const { roReason } = usePage();
  return {
    roReason,
    canWrite: roReason === null,
    /** Props for a control that writes. */
    wr: (fn: (e: MouseEvent) => void) => ({
      onClick: (e: MouseEvent) => {
        pre(e);
        if (roReason) return;
        fn(e);
      },
      'aria-disabled': roReason ? true : undefined,
      title: roReason ?? undefined,
    }),
  };
}

/** "+15125550147" -> "+1 (512) •••-0147" (the design's masked number); an already masked one stays. */
export function maskPhone(p: string): string {
  if (p.includes('•') || p.includes('*')) return p;
  const d = p.replace(/\D/g, '');
  if (d.length === 11 && d[0] === '1') return '+1 (' + d.slice(1, 4) + ') •••-' + d.slice(7);
  return p.slice(0, 3) + '•••' + p.slice(-4);
}

/** "HH:MM", the design's stamp() (L21865). */
export const hhmm = (d: Date) => String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');

/** Download a response body as a file (settings.json, the audit exports). */
export async function downloadFrom(client: ApiClient, path: string, name: string, method: 'GET' | 'POST' = 'GET'): Promise<void> {
  const res = await client.raw(path, { method });
  if (!res.ok) {
    let msg = `The export failed (${res.status}).`;
    try {
      const j = await res.json();
      if (j && typeof j.error === 'string') msg = j.error;
    } catch {
      /* not JSON */
    }
    throw Object.assign(new Error(msg), { status: res.status, message: msg, requestId: res.headers.get('x-request-id') });
  }
  const blob = await res.blob();
  if (typeof URL.createObjectURL !== 'function') return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}
